import React, { useEffect, useState } from 'react';
import { collection, addDoc, getDocs, query, orderBy, Timestamp, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase'; // Import auth to get current user
import { onAuthStateChanged } from 'firebase/auth';
import './Logbook.css'; // Import the CSS file

// Helper to format Firestore Timestamp
const formatLogTimestamp = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toLocaleString('id-ID', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  }
  return 'Invalid Date';
};

const Logbook = () => {
  const [entries, setEntries] = useState([]);
  const [newEntry, setNewEntry] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [userName, setUserName] = useState('');

  // Get current user info
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        // Fetch user's name from Firestore 'users' collection
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUserName(userSnap.data().nama || user.email); // Use name or fallback to email
        } else {
          setUserName(user.email); // Fallback if user doc not found
        }
      } else {
        setCurrentUser(null);
        setUserName('');
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch logbook entries
  const fetchEntries = async () => {
    setLoading(true);
    setError('');
    try {
      const entriesRef = collection(db, 'logbook_laboran');
      const q = query(entriesRef, orderBy('timestamp', 'desc')); // Show newest first
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      setEntries(data);
    } catch (err) {
      console.error("Error fetching logbook entries:", err);
      setError('Gagal memuat entri logbook.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch entries when component mounts
  useEffect(() => {
    fetchEntries();
  }, []);

  // Handle adding a new entry
  const handleAddEntry = async (e) => {
    e.preventDefault();
    if (!newEntry.trim() || !currentUser) {
      setError('Catatan tidak boleh kosong dan Anda harus login.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const entryData = {
        catatan: newEntry,
        timestamp: Timestamp.now(),
        userId: currentUser.uid,
        namaUser: userName, // Use the fetched user name
      };
      const docRef = await addDoc(collection(db, 'logbook_laboran'), entryData);

      // Add new entry to the top of the list locally
      setEntries(prevEntries => [{ id: docRef.id, ...entryData }, ...prevEntries]);
      setNewEntry(''); // Clear the textarea
      console.log("Logbook entry added:", docRef.id);
    } catch (err) {
      console.error("Error adding logbook entry:", err);
      setError('Gagal menambahkan entri logbook.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="logbook-container">
      <h2>Logbook Laboran</h2>

      {/* Form for new entry */}
      <form onSubmit={handleAddEntry} className="logbook-form">
        <textarea
          placeholder="Tambahkan catatan logbook baru..."
          value={newEntry}
          onChange={(e) => setNewEntry(e.target.value)}
          required
          disabled={!currentUser || loading}
        />
        <button type="submit" disabled={!currentUser || loading || !newEntry.trim()}>
          {loading ? 'Menyimpan...' : 'Tambah Entri'}
        </button>
      </form>

      {error && <div className="error-message">{error}</div>}

      {/* Display existing entries */}
      {loading && entries.length === 0 && <div className="loading-message">Memuat entri...</div>}

      <ul className="logbook-list">
        {entries.map(entry => (
          <li key={entry.id} className="logbook-entry">
            <div className="logbook-entry-header">
              <span className="logbook-entry-user">{entry.namaUser || 'Unknown User'}</span>
              <span className="logbook-entry-timestamp">{formatLogTimestamp(entry.timestamp)}</span>
            </div>
            <p className="logbook-entry-notes">{entry.catatan}</p>
          </li>
        ))}
        {entries.length === 0 && !loading && (
          <p style={{ textAlign: 'center', color: '#777' }}>Belum ada entri logbook.</p>
        )}
      </ul>
    </div>
  );
};

export default Logbook;
